"use client"

import useEmblaCarousel from "embla-carousel-react"
import { useCallback, useEffect, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/common"

interface RelatedProductsCarouselProps {
  children: React.ReactNode
}

export default function RelatedProductsCarousel({ children }: RelatedProductsCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  })

  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)
  }, [emblaApi, onSelect])

  return (
    <div className="relative group">
      {/* Navigation arrows */}
      {canScrollPrev && (
        <Button
          variant="ghost"
          onClick={scrollPrev}
          className="h-auto p-0 rounded-none font-normal absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background border border-border shadow-md flex items-center justify-center hover:bg-surface transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
          aria-label="Previous"
          type="button"
        >
          <ChevronLeft className="w-5 h-5 text-foreground" />
        </Button>
      )}
      {canScrollNext && (
        <Button
          variant="ghost"
          onClick={scrollNext}
          className="h-auto p-0 rounded-none font-normal absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background border border-border shadow-md flex items-center justify-center hover:bg-surface transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
          aria-label="Next"
          type="button"
        >
          <ChevronRight className="w-5 h-5 text-foreground" />
        </Button>
      )}

      {/* Carousel viewport */}
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {children}
        </div>
      </div>
    </div>
  )
}
